import * as LDClient from "launchdarkly-js-client-sdk";
import UAParser from "ua-parser-js";
import { v4 as uuidv4 } from "uuid";

import { analytics } from "../utils/analytics";
import { isAuthenticated } from "../utils/currentUser";
import { beforeNavigate } from "../utils/navigate";
import intlService from "./intl";

import * as Sentry from "@sentry/gatsby";
import { fsInspector } from "./launchDarklyInspectors/fullstory";
import { sessionInspector } from "./launchDarklyInspectors/session";

interface UserContext {
  kind: "user";
  key: string;
  name?: string;
  email?: string;
  cart_id?: string;
  is_authenticated?: boolean;
  _meta?: {
    privateAttributes: string[];
  };
}

interface DeviceContext {
  kind: "device";
  key: string;
  device: Record<string, any>;
  query_string: Record<string, any>;
  store: Record<string, any>;
  cart_id?: string;
  session_id: string;
  _meta?: {
    privateAttributes: string[];
  };
}

interface MultiContext {
  kind: "multi";
  user: UserContext;
  device: DeviceContext;
}

export class LaunchDarklyInstance {
  _ldClient?: LDClient.LDClient;
  _context?: MultiContext | DeviceContext;
  private _variationCache = new Map<string, any>();
  ldOptions: LDClient.LDOptions = {
    evaluationReasons: true,
    fetchGoals: false,
    sendEventsOnlyForVariation: true,
    bootstrap: (window as any)?.ldFlags,
    inspectors: [
      Sentry.buildLaunchDarklyFlagUsedHandler(),
      fsInspector,
      sessionInspector,
    ],
    application: {
      id: "home",
    },
  };

  private getLDOptions(): LDClient.LDOptions {
    return {
      ...this.ldOptions,
      ...(process.env.GATSBY_LAUNCHDARKLY_RELAY_PROXY_URL && {
        streamUrl: process.env.GATSBY_LAUNCHDARKLY_RELAY_PROXY_URL,
        baseUrl: process.env.GATSBY_LAUNCHDARKLY_RELAY_PROXY_URL,
        eventsUrl: process.env.GATSBY_LAUNCHDARKLY_RELAY_PROXY_URL,
      }),
    };
  }

  get ldClient() {
    if (!this._ldClient) {
      throw new Error("LaunchDarkly client not initialized");
    }

    return this._ldClient;
  }

  get context() {
    if (!this._context) {
      throw new Error("LaunchDarkly context not initialized");
    }

    return this._context;
  }

  async initialize(): Promise<void> {
    this._context = this.getSetContext();

    if (!process.env.GATSBY_LAUNCHDARKLY_CLIENT_ID) {
      throw new Error("GATSBY_LAUNCHDARKLY_CLIENT_ID is required");
    }

    // eslint-disable-next-line no-console
    console.log("Initializing LaunchDarkly client");
    // eslint-disable-next-line no-console
    console.log("Context", this.context);

    this._ldClient = LDClient.initialize(
      process.env.GATSBY_LAUNCHDARKLY_CLIENT_ID,
      this.context as
        | LDClient.LDSingleKindContext
        | LDClient.LDMultiKindContext,
      this.getLDOptions(),
    );

    // eslint-disable-next-line no-console
    console.log("LaunchDarkly client initialized");

    return this.ldClient.waitUntilReady().then(() => {
      this.setAllFlagsInStorage();
    });
  }

  private setAllFlagsInStorage(): void {
    const flags = this.ldClient.allFlags();
    for (const [key, value] of Object.entries(flags)) {
      sessionStorage.setItem(`ldFlag:${key}`, JSON.stringify(value));
    }
  }

  private getSetContext(
    attributes: Record<string, any> = { user: {}, device: {} },
  ): MultiContext | DeviceContext {
    let userInfo = analytics.getStoredUserInfo();
    return (this._context = userInfo.id
      ? this.multiContext(attributes)
      : this.deviceContext(attributes.device));
  }

  private multiContext(
    attributes: Record<string, any> = { user: {}, device: {} },
  ): MultiContext {
    return {
      kind: "multi",
      user: this.userContext(attributes.user),
      device: this.deviceContext(attributes.device),
    };
  }

  // Construct the userContext object and set it on the singleton
  private userContext(attributes: Record<string, any> = {}): UserContext {
    let userInfo = analytics.getStoredUserInfo();
    let authenticated = isAuthenticated() as boolean;

    return {
      kind: "user",
      key: userInfo.id,
      name: userInfo.traits.first_name + " " + userInfo.traits.last_name,
      email: userInfo.traits.email,
      cart_id: userInfo.traits.cart_id,
      is_authenticated: authenticated,
      ...attributes,
      _meta: {
        privateAttributes: ["email"],
      },
    };
  }

  private deviceContext(attributes: Record<string, any> = {}): DeviceContext {
    let userInfo = analytics.getStoredUserInfo();
    let uaParser = new UAParser(navigator.userAgent);
    let params = new URLSearchParams(window.location.search);

    let anonymousId = userInfo.anonymousId;

    if (!anonymousId) {
      anonymousId = params.get("ajs_aid") || uuidv4();
      analytics.setAnonymousId(anonymousId);
    }

    const session_id = sessionStorage.getItem("session_id") || uuidv4();
    sessionStorage.setItem("session_id", session_id);

    return {
      kind: "device",
      key: anonymousId,
      session_id,
      device: {
        client: uaParser.getBrowser().name || null,
        os_name: uaParser.getOS().name || null,
        os_version: uaParser.getOS().version || null,
        device_name: uaParser.getDevice().model || null,
        device_type: uaParser.getDevice().type || null,
      },
      query_string: {
        utm_campaign: params.get("utm_campaign") || null,
        utm_source: params.get("utm_source") || null,
        utm_medium: params.get("utm_medium") || null,
        utm_content: params.get("utm_content") || null,
      },
      store: {
        locale: intlService.locale,
        currency: intlService.currency.type,
      },
      cart_id: userInfo?.traits?.cart_id,
      ...attributes,
    };
  }

  variation(featureKey: string, defaultValue: any = false): any {
    // Create cache key that includes both feature key and default value
    const cacheKey = `${featureKey}:${JSON.stringify(defaultValue)}`;

    // Check cache first
    if (this._variationCache.has(cacheKey)) {
      return this._variationCache.get(cacheKey);
    }

    if (!this.ldClient) return defaultValue;

    const detail = this.ldClient.variationDetail(featureKey, defaultValue);
    // eslint-disable-next-line no-console
    console.log("Getting variation for feature", featureKey);
    const value = detail.value;

    // Cache the result
    this._variationCache.set(cacheKey, value);

    return value;
  }

  track(event: string, properties: any): void {
    if (!this.ldClient) return;

    return this.ldClient.track(event, properties);
  }

  async identify(
    attributes: Record<string, any> = { user: {}, device: {} },
  ): Promise<void> {
    if (!this.ldClient) return;

    this.getSetContext(attributes);

    // eslint-disable-next-line no-console
    console.log("Identifying user with context", this.context);

    // Clear cache when user context changes
    this.clearVariationCache();

    await this.ldClient.identify(this.context);
  }

  async flush(): Promise<void> {
    return this.ldClient.flush();
  }

  clearVariationCache(): void {
    this._variationCache.clear();
  }

  clearVariationCacheForKey(featureKey: string): void {
    // Clear all cache entries for the exact feature key (with any default value)
    const keysToDelete: string[] = [];

    // First, collect all keys that match the exact feature key pattern
    const prefix = `${featureKey}:`;

    for (const key of this._variationCache.keys()) {
      if (key.startsWith(prefix)) {
        keysToDelete.push(key);
      }
    }

    // Then delete them to avoid modifying the Map while iterating
    for (const key of keysToDelete) {
      this._variationCache.delete(key);
    }
  }
}

let launchDarkly: LaunchDarklyInstance | null = null;
if (typeof window !== "undefined") {
  launchDarkly = new LaunchDarklyInstance();

  beforeNavigate(async () => {
    await launchDarkly?.flush();
  });
}

export function variation(featureKey: string, defaultValue: any = false): any {
  return launchDarkly && launchDarkly.variation(featureKey, defaultValue);
}

export function track(event: string, properties: any): void {
  launchDarkly && launchDarkly.track(event, properties);
}

export async function identify(attributes: { user: {}; device: {} }) {
  launchDarkly && launchDarkly.identify(attributes);
}

export async function flush() {
  launchDarkly && launchDarkly.flush();
}

export function clearVariationCache() {
  launchDarkly && launchDarkly.clearVariationCache();
}

export function clearVariationCacheForKey(featureKey: string) {
  launchDarkly && launchDarkly.clearVariationCacheForKey(featureKey);
}

export default launchDarkly;
